import DataSelectionModel from "../DataSelectionModal";
import * as yup from "yup";
import Form from "react-bootstrap/Form";
import { WfsGetFeatureRequest } from "../../../types/Wfs/WfsGetFeatureRequest";
import { useDataSelectionContext } from "../DataSelectionContext";
import { useMemo } from "react";
import { FormGroup } from "react-bootstrap";
import OsmPoiClassificationsField from "./OsmPoiClassificationsField";
import Classifications, { Classification } from "./OsmPoiClassifications";

interface OsmPoiFormValues {
  classifications: number[];
  outputFormat: string;
}

const OsmPoi = () => {
  const layerName = "osm:poi";
  const { layers, getLayer, wfsOrder } = useDataSelectionContext();
  const layer = useMemo(() => getLayer(layerName), [layers]);
  const initialValues = useMemo(
    () =>
      ({
        classifications: [],
        outputFormat: "shapefile",
      } as OsmPoiFormValues),
    [layer],
  );
  const schema = useMemo(
    () =>
      yup.object({
        classifications: yup
          .array()
          .min(1, "You must select at least one category or classification"),
        outputFormat: yup
          .string()
          .oneOf(["shapefile", "csv", "gml"])
          .required(),
      }),
    [layer],
  );

  const onSubmit = (values: OsmPoiFormValues) => {
    const selectedClasses = findSelectedClassifications(
      values.classifications,
      Classifications,
    );
    const filterClauses = [];
    filterClauses.push(
      ...selectedClasses
        .filter((c) => c.Subclasses)
        .map((c) => `Classification SUBCLASS_OF ${c.Code}`),
    );
    const granularClasses = selectedClasses
      .filter((c) => !c.Subclasses)
      .map((c) => c.Code);
    if (granularClasses.length)
      filterClauses.push(`Classification IN ${granularClasses.join(", ")}`);
    const filter = filterClauses.join(" OR ");

    wfsOrder({
      service: "WFS",
      version: "2.0.0",
      request: "GetFeature",
      resultType: "Results",
      typeNames: layerName,
      outputFormat: values.outputFormat,
      filter: filter,
    } as WfsGetFeatureRequest);
  };

  // Gets a list of the selected classifications
  const findSelectedClassifications = (
    classCodes: number[],
    classifications: Classification[],
  ): Classification[] => {
    const selected = [];
    for (let classification of classifications) {
      if (classCodes.some((c) => c === classification.Code)) {
        selected.push(classification);
      }

      if (classification.Subclasses) {
        selected.push(
          ...findSelectedClassifications(classCodes, classification.Subclasses),
        );
      }
    }

    return selected;
  };

  return (
    <DataSelectionModel
      headerText="OSM Points of Interest"
      layer={layer}
      size="xl"
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit}>
      {({ values, errors, handleChange, handleBlur }) => (
        <>
          <FormGroup>
            <OsmPoiClassificationsField />
            {errors.classifications && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.classifications}
              </Form.Control.Feedback>
            )}
          </FormGroup>

          <Form.Group style={{ width: 300 }}>
            <Form.Label htmlFor="outputFormat">Format</Form.Label>
            <Form.Select
              id="outputFormat"
              name="outputFormat"
              value={values.outputFormat}
              onChange={handleChange}
              onBlur={handleBlur}>
              <option value="gml">GML 3</option>
              <option value="shapefile">Shapefile</option>
              <option value="csv">CSV</option>
            </Form.Select>
          </Form.Group>
        </>
      )}
    </DataSelectionModel>
  );
};

export default OsmPoi;
